(() => {
  const rootSelector = '[data-deferred-product-description]';
  const templateSelector = 'template[data-product-description-template]';
  const frameSelector = '[data-description-image-frame]';
  const stateAttribute = 'data-description-image-state';
  const retryLabel = window.yeswoodProductStrings?.imageRetry || 'Image failed to load. Click to retry.';
  const frameControllers = new WeakMap();
  const originalSources = new WeakMap();
  const sourceTransitions = new WeakSet();
  const responsiveWidths = [375, 600, 750];
  const responsiveSizes = '(min-width: 1320px) 732px, (min-width: 990px) calc(62vw - 8.7rem), calc(100vw - 3rem)';
  const supportedImageExtension = /\.(?:jpg|jpeg|png|webp|avif)$/i;
  const isPositiveIntegerDimension = (value) => {
    if (typeof value !== 'string') return false;
    const normalized = value.trim();
    return /^\d+$/.test(normalized) && /[1-9]/.test(normalized);
  };

  const hasImageSource = (image) => Boolean(image.getAttribute('src') || image.getAttribute('srcset'));

  const rememberSources = (image) => {
    if (!originalSources.has(image)) {
      originalSources.set(image, {
        src: image.getAttribute('src'),
        srcset: image.getAttribute('srcset'),
        sizes: image.getAttribute('sizes'),
        optimized: false,
        fallbackAttempted: false,
      });
    }
    return originalSources.get(image);
  };

  const shopifyImageUrl = (src) => {
    if (typeof src !== 'string' || !src) return null;
    try {
      const rawPathname = src
        .replace(/^[a-z][a-z\d+.-]*:\/\/[^/]*|^\/\/[^/]+/i, '')
        .split(/[?#]/, 1)[0];
      const decodedSourcePathname = decodeURIComponent(rawPathname);
      if (/%(?:2f|5c)/i.test(rawPathname)) return null;
      if (decodedSourcePathname.includes('\\')) return null;
      if (decodedSourcePathname.split('/').some((segment) => segment === '.' || segment === '..')) return null;

      const url = new URL(src, window.location.origin);
      if (/%(?:2f|5c)/i.test(url.pathname)) return null;

      const decodedPathname = decodeURIComponent(url.pathname);
      if (decodedPathname.includes('\\')) return null;
      if (decodedPathname.split('/').some((segment) => segment === '.' || segment === '..')) return null;

      const isShopifyFiles = url.origin === 'https://cdn.shopify.com'
        && decodedPathname.startsWith('/s/files/');
      const isSameOriginFiles = url.origin === window.location.origin
        && decodedPathname.startsWith('/cdn/shop/files/');

      if ((!isShopifyFiles && !isSameOriginFiles) || !supportedImageExtension.test(decodedPathname)) return null;
      return url;
    } catch {
      return null;
    }
  };

  const optimizableImageUrl = (image, source) => {
    if (!source?.src || source.srcset !== null || image.closest?.('picture')) return null;
    return shopifyImageUrl(source.src);
  };

  const resizedImageUrl = (url, width) => {
    const candidate = new URL(url.href);
    candidate.searchParams.set('width', String(width));
    return candidate.href;
  };

  const previewImageKey = (src) => {
    const url = shopifyImageUrl(src);
    if (!url || [...url.searchParams.keys()].some((key) => key !== 'width' && key !== 'v')) return null;
    return `${url.pathname.split('/files/').pop()}?v=${url.searchParams.get('v') || ''}`;
  };

  const galleryPreviewRatios = (root) => {
    const product = root.closest('product-info')
      || root.closest('.yeswood-product-stream')?.querySelector('.yeswood-product-stream__main')?.querySelector('product-info');
    const ratios = new Map();
    product?.querySelector('media-gallery')?.querySelectorAll('.product-media-container.media-type-image').forEach((media) => {
      const image = media.querySelector('img');
      const key = image && previewImageKey(image.getAttribute('src'));
      const ratio = Number(media.style.getPropertyValue('--preview-ratio'));
      if (key && Number.isFinite(ratio) && ratio > 0) ratios.set(key, ratio);
    });
    try {
      const entries = JSON.parse(root.querySelector('[data-product-image-ratios]')?.textContent || '[]');
      if (Array.isArray(entries)) entries.forEach((entry) => {
        if (!Array.isArray(entry)) return;
        const [src, ratio] = entry;
        const key = previewImageKey(src);
        if (key && Number.isFinite(ratio) && ratio > 0) ratios.set(key, ratio);
      });
    } catch { /* Existing gallery ratios remain available if metadata is invalid. */ }
    return ratios;
  };

  const applyResponsiveSources = (image, source = rememberSources(image)) => {
    const url = optimizableImageUrl(image, source);
    if (!url) return false;

    const candidates = responsiveWidths.map((width) => `${resizedImageUrl(url, width)} ${width}w`);
    image.setAttribute('src', resizedImageUrl(url, 750));
    image.setAttribute('srcset', candidates.join(', '));
    image.setAttribute('sizes', responsiveSizes);
    image.setAttribute('data-description-responsive-image', '');
    source.optimized = true;
    source.fallbackAttempted = false;
    sourceTransitions.delete(image);
    return true;
  };

  const scheduleSourceUpdate = (callback) => {
    const schedule = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : (nextCallback) => window.setTimeout(nextCallback, 0);
    schedule(callback);
  };

  const restoreAttribute = (image, name, value) => {
    if (value === null) image.removeAttribute(name);
    else image.setAttribute(name, value);
  };

  const clearRequestSources = (image) => {
    sourceTransitions.add(image);
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.removeAttribute('src');
  };

  const restoreOriginalSources = (image, source = originalSources.get(image)) => {
    if (!source) {
      sourceTransitions.delete(image);
      return;
    }
    restoreAttribute(image, 'src', source.src);
    restoreAttribute(image, 'srcset', source.srcset);
    restoreAttribute(image, 'sizes', source.sizes);
    image.removeAttribute('data-description-responsive-image');
    source.optimized = false;
    sourceTransitions.delete(image);
  };

  const fallbackToOriginal = (image) => {
    const source = originalSources.get(image);
    if (!source?.optimized || source.fallbackAttempted) return false;

    source.fallbackAttempted = true;
    clearRequestSources(image);
    scheduleSourceUpdate(() => restoreOriginalSources(image, source));
    return true;
  };

  const imageState = (image) => {
    if (image.isConnected === false && !image.currentSrc) return 'loading';
    if (!image.complete) return 'loading';
    return image.naturalWidth > 0 ? 'loaded' : 'error';
  };

  const mediaGroupNode = (image) => {
    const mediaNode = image.closest?.('picture') || image;
    return mediaNode.closest?.('a') || mediaNode;
  };

  const setFrameState = (frame, retryButton, state) => {
    frame.setAttribute(stateAttribute, state);
    if (state === 'loading') frame.setAttribute('aria-busy', 'true');
    else frame.removeAttribute('aria-busy');

    const isError = state === 'error';
    retryButton.hidden = !isError;
    retryButton.textContent = isError ? retryLabel : '';
  };

  const wrapMediaGroup = (groupNode) => {
    if (groupNode.parentNode?.matches?.(frameSelector)) return groupNode.parentNode;

    const frame = document.createElement('span');
    frame.classList.add('yeswood-description-image-frame');
    frame.setAttribute('data-description-image-frame', '');

    if (typeof groupNode.replaceWith === 'function') groupNode.replaceWith(frame);
    else groupNode.parentNode?.replaceChild?.(frame, groupNode);
    frame.appendChild(groupNode);

    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.hidden = true;
    retryButton.classList.add('yeswood-description-image-frame__retry');
    retryButton.setAttribute('data-description-image-retry', '');
    retryButton.setAttribute('aria-live', 'polite');
    frame.appendChild(retryButton);
    return frame;
  };

  const initializeMediaGroup = (groupNode, images) => {
    const frame = wrapMediaGroup(groupNode);
    if (!frame || frameControllers.has(frame)) return;

    const retryButton = frame.querySelector('[data-description-image-retry]');
    images.forEach((image) => {
      rememberSources(image);
      image.classList.add('yeswood-description-image-frame__image');
    });

    const sync = () => {
      const states = images.map((image) => (
        sourceTransitions.has(image) ? 'loading' : imageState(image)
      ));
      if (states.includes('error')) setFrameState(frame, retryButton, 'error');
      else if (states.every((state) => state === 'loaded')) setFrameState(frame, retryButton, 'loaded');
      else setFrameState(frame, retryButton, 'loading');
    };

    const retryFailedImages = () => {
      const failedImages = images.filter(
        (image) => imageState(image) === 'error' && !sourceTransitions.has(image),
      );
      if (!failedImages.length) return;

      setFrameState(frame, retryButton, 'loading');
      failedImages.forEach((image) => {
        const source = rememberSources(image);
        source.fallbackAttempted = false;
        source.optimized = false;
        clearRequestSources(image);
      });

      scheduleSourceUpdate(() => {
        failedImages.forEach((image) => {
          const source = rememberSources(image);
          if (!applyResponsiveSources(image, source)) restoreOriginalSources(image, source);
        });
      });
    };

    const syncErrorOrFallback = () => {
      let fallbackStarted = false;
      let terminalError = false;
      images
        .filter((image) => imageState(image) === 'error' && !sourceTransitions.has(image))
        .forEach((image) => {
          if (fallbackToOriginal(image)) fallbackStarted = true;
          else terminalError = true;
        });

      if (terminalError) sync();
      else if (fallbackStarted) setFrameState(frame, retryButton, 'loading');
      else sync();
    };

    images.forEach((image) => {
      image.addEventListener('load', () => {
        if (sourceTransitions.has(image)) return;
        sync();
      });
      image.addEventListener('error', () => {
        if (sourceTransitions.has(image)) return;
        syncErrorOrFallback();
      });
    });
    retryButton.addEventListener('click', retryFailedImages);
    frameControllers.set(frame, { sync, retryFailedImages });
    syncErrorOrFallback();
  };

  const setImageHints = (content, previewRatios) => {
    const groups = new Map();
    content.querySelectorAll('img').forEach((image) => {
      if (image.hasAttribute('alt')) image.setAttribute('alt', image.getAttribute('alt').replace(/\s*\[local:[^\]]*\]/g, '').trim());
      const source = rememberSources(image);
      applyResponsiveSources(image, source);
      image.setAttribute('loading', 'lazy');
      image.setAttribute('decoding', 'async');
      if (!image.hasAttribute('fetchpriority')) image.setAttribute('fetchpriority', 'low');
      const hasCompleteDimensions = isPositiveIntegerDimension(image.getAttribute('width'))
        && isPositiveIntegerDimension(image.getAttribute('height'));
      if (!hasCompleteDimensions && !image.style.aspectRatio) {
        const key = image.closest?.('picture') ? null : previewImageKey(image.getAttribute('src'));
        image.style.aspectRatio = `auto ${previewRatios.get(key) || '3 / 4'}`;
      }
      if (!hasCompleteDimensions && !image.style.width) image.style.width = '100%';

      if (!hasImageSource(image)) return;
      const groupNode = mediaGroupNode(image);
      if (!groups.has(groupNode)) groups.set(groupNode, []);
      groups.get(groupNode).push(image);
    });

    groups.forEach((images, groupNode) => initializeMediaGroup(groupNode, images));
  };

  const createFallbackContent = (template) => {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = template.innerHTML || '';

    const content = document.createDocumentFragment();
    while (wrapper.firstChild) content.appendChild(wrapper.firstChild);
    return content;
  };

  const hydrate = (root) => {
    if (!root || root.dataset.descriptionHydrated === 'true') return;

    const template = root.querySelector(templateSelector);
    if (!template) return;

    const canCloneTemplate = template.content && typeof template.content.cloneNode === 'function';
    const canReplaceTemplate = typeof template.replaceWith === 'function';
    const content = canCloneTemplate ? template.content.cloneNode(true) : createFallbackContent(template);

    setImageHints(content, galleryPreviewRatios(root));

    if (canReplaceTemplate) {
      template.replaceWith(content);
    } else if (template.parentNode && typeof template.parentNode.replaceChild === 'function') {
      template.parentNode.replaceChild(content, template);
    } else {
      root.appendChild(content);
    }
    root.dataset.descriptionHydrated = 'true';
  };

  const initialize = (scope) => {
    if (scope?.matches?.(rootSelector)) hydrate(scope);
    scope?.querySelectorAll?.(rootSelector).forEach(hydrate);
  };

  // This script is deferred: the DOM is parsed. Reserve description structure now;
  // waiting for unrelated DOMContentLoaded work lets lower sections paint then jump.
  // Image requests still use native lazy loading and the existing retry lifecycle.
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
